(() => {
  'use strict';

  const ELIGIBLE_TAG = 'muismat';
  const rawMode = String(window.LEVELY_BOGO_MODE || 'off').trim().toLowerCase();
  const MODE = ['2plus1', '1plus1'].includes(rawMode) ? rawMode : 'off';
  const GROUP_SIZE = MODE === '1plus1' ? 2 : 3;
  const PROGRESS_SELECTOR = '[data-levely-progress]';
  const CART_SELECTOR = [
    '#Main-Cart-Details',
    '#Cart-Drawer-Details',
    'cart-drawer',
    'cart-items',
    '.cart-details'
  ].join(',');

  const productTagsByHandle = new Map();
  let updateTimer;
  let latestUpdate = 0;

  function shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  async function fetchJson(url) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Levely progress bar request failed: ${response.status}`);
    }

    return response.json();
  }

  function getProductHandle(cartItem) {
    if (cartItem?.handle) return cartItem.handle;

    const match = cartItem?.url?.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function getProductTags(handle) {
    if (!handle) return Promise.resolve([]);
    if (productTagsByHandle.has(handle)) return productTagsByHandle.get(handle);

    const request = fetchJson(
      `${shopRoot()}products/${encodeURIComponent(handle)}.js`
    )
      .then((product) => (Array.isArray(product.tags) ? product.tags : []))
      .catch((error) => {
        productTagsByHandle.delete(handle);
        throw error;
      });

    productTagsByHandle.set(handle, request);
    return request;
  }

  async function countEligibleItems(cart) {
    if (!Array.isArray(cart?.items)) return 0;

    const quantities = await Promise.all(
      cart.items.map(async (item) => {
        const tags = await getProductTags(getProductHandle(item));
        const isEligible = tags.some(
          (tag) => String(tag).trim().toLowerCase() === ELIGIBLE_TAG
        );

        return isEligible ? Number(item.quantity) || 0 : 0;
      })
    );

    return quantities.reduce((total, quantity) => total + quantity, 0);
  }

  function getCopy() {
    const isDutch = (document.documentElement.lang || '')
      .toLowerCase()
      .startsWith('nl');

    return isDutch
      ? {
          step1: '🎁 Voeg nog 1 muismat toe en krijg er 1 gratis!',
          step2: '🎁 Voeg een 3e muismat toe. Deze is gratis!',
          unlocked: '🎁 Gratis muismat unlocked!'
        }
      : {
          step1: '🎁 Add one more mousepad and get one FREE!',
          step2: '🎁 Add a third mousepad. This one is FREE!',
          unlocked: '🎁 FREE mousepad unlocked!'
        };
  }

  function getProgressState(quantity) {
    const remainder = quantity % GROUP_SIZE;
    const unlocked = quantity > 0 && remainder === 0;

    return {
      remainder,
      unlocked,
      percentage: unlocked ? 100 : (remainder / GROUP_SIZE) * 100,
      freeCount: Math.floor(quantity / GROUP_SIZE)
    };
  }

  function renderProgress(cart, quantity) {
    const copy = getCopy();
    const state = getProgressState(quantity);

    document.querySelectorAll(PROGRESS_SELECTOR).forEach((wrapper) => {
      const message = wrapper.querySelector('.progress-offer-message');
      const bar = wrapper.querySelector('.progress-offer-bar');
      const fill = wrapper.querySelector('.progress-offer-fill');

      if (!message || !bar || !fill) return;

      wrapper.hidden = MODE === 'off' || Number(cart.item_count) === 0 || quantity === 0;
      bar.setAttribute('aria-label', MODE === '1plus1' ? '1+1 gratis' : '2+1 gratis');
      fill.style.width = `${state.percentage}%`;
      bar.setAttribute('aria-valuenow', String(Math.round(state.percentage)));

      if (state.unlocked) {
        message.textContent = `${copy.unlocked} (${state.freeCount})`;
        wrapper.dataset.state = 'unlocked';
      } else if (state.remainder === 2) {
        message.textContent = copy.step2;
        delete wrapper.dataset.state;
      } else {
        message.textContent = copy.step1;
        delete wrapper.dataset.state;
      }
    });
  }

  async function updateProgress() {
    const updateId = ++latestUpdate;

    if (MODE === 'off') {
      document.querySelectorAll(PROGRESS_SELECTOR).forEach((wrapper) => {
        wrapper.hidden = true;
      });
      return;
    }
    if (!document.querySelector(PROGRESS_SELECTOR)) return;

    try {
      const cart = await fetchJson(`${shopRoot()}cart.js`);
      const quantity = await countEligibleItems(cart);

      if (updateId !== latestUpdate) return;
      renderProgress(cart, quantity);
    } catch (error) {
      if (updateId !== latestUpdate) return;
      document.querySelectorAll(PROGRESS_SELECTOR).forEach((wrapper) => {
        wrapper.hidden = true;
      });
      console.warn('[Levely progress bar]', error);
    }
  }

  function scheduleUpdate() {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(updateProgress, 150);
  }

  function containsCart(node) {
    if (!(node instanceof Element)) return false;
    return node.matches(CART_SELECTOR) || Boolean(node.querySelector(CART_SELECTOR));
  }

  function watchCart() {
    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      const cartChanged = mutations.some((mutation) => {
        const target = mutation.target instanceof Element
          ? mutation.target
          : mutation.target.parentElement;

        if (target?.closest(PROGRESS_SELECTOR)) return false;
        if (target?.closest(CART_SELECTOR)) return true;

        return [...mutation.addedNodes, ...mutation.removedNodes].some(containsCart);
      });

      if (cartChanged) scheduleUpdate();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    updateProgress();
    watchCart();

    document.addEventListener('cart:updated', scheduleUpdate);
    document.addEventListener('cart:refresh', scheduleUpdate);
    document.addEventListener('shopify:section:load', scheduleUpdate);
    window.addEventListener('pageshow', scheduleUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();